import { useState, useEffect, useRef, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Area, AreaChart } from 'recharts';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { Transaction, Category } from '../types';
import BankRequestModal from '../components/BankRequestModal';
import AnalyticsDashboardModal from '../components/AnalyticsDashboardModal';
import OfflineIndicator from '../components/OfflineIndicator';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Footer from '../components/Footer';

export default function HomePage() {
  const { t } = useTranslation();

  // Upload state
  const [files, setFiles] = useState<File[]>([]);
  const [detectedBankName, setDetectedBankName] = useState<string>('');
  const [detecting, setDetecting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [bankRequestModalOpen, setBankRequestModalOpen] = useState(false);
  const [failedStatementFile, setFailedStatementFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Transactions state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [autoCategorizeComplete, setAutoCategorizeComplete] = useState(false);

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Analytics modal state
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);

  // Chart filter state
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all-time');

  // Collapsible month state - track which months are expanded
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // Track previous transaction count to detect uploads vs updates
  const prevTransactionCountRef = useRef<number>(0);

  // Load initial data
  useEffect(() => {
    // Check authentication status
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);

    loadCategories();
    loadAllTransactions();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadAllTransactions = async () => {
    setLoading(true);
    try {
      const data = await api.getAllTransactions();
      setTransactions(data.transactions || []);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Upload handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter(
        file => file.type === 'application/pdf'
      );

      if (droppedFiles.length > 0) {
        setFiles(droppedFiles);
        setUploadError(null);
        setFailedStatementFile(null); // Clear failed statement state
        // Detect bank from first file
        detectBankFromFile(droppedFiles[0]);
      } else {
        setUploadError('Please upload PDF files only');
      }
    }
  };

  const detectBankFromFile = async (file: File) => {
    console.log('🔍 Starting bank detection for:', file.name);
    setDetecting(true);
    setUploadError(null);
    setDetectedBankName('');
    setFailedStatementFile(null);

    try {
      const result = await api.detectBankFromStatement(file);
      console.log('🔍 Detection result:', result);

      if (result.detected && result.bank) {
        console.log('✅ Bank detected:', result.bank.name, 'ID:', result.bank.id);
        setDetectedBankName(result.bank.name);
      } else {
        console.log('❌ No bank detected');
        setUploadError(result.message || 'Could not detect bank automatically.');
        setFailedStatementFile(file); // Store the file for bank request
      }
    } catch (err: any) {
      console.error('❌ Bank detection failed:', err);
      setUploadError('Could not detect bank from this PDF.');
      setFailedStatementFile(file); // Store the file for bank request
    } finally {
      setDetecting(false);
      console.log('🔍 Detection complete');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files).filter(
        file => file.type === 'application/pdf'
      );

      if (selectedFiles.length > 0) {
        setFiles(selectedFiles);
        setUploadError(null);
        setFailedStatementFile(null); // Clear failed statement state
        // Detect bank from first file
        detectBankFromFile(selectedFiles[0]);
      } else {
        setUploadError('Please upload PDF files only');
      }
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setUploadError('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);

    const errors: string[] = [];
    let successCount = 0;
    let detectionFailures = 0;

    try {
      // Upload all files sequentially, detecting bank for each one
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`🔍 Processing ${i + 1}/${files.length}: ${file.name}`);

        try {
          // Detect bank for this specific file
          const detectionResult = await api.detectBankFromStatement(file);

          if (detectionResult.detected && detectionResult.bank) {
            console.log(`✅ Detected ${detectionResult.bank.name} for ${file.name}`);
            // Upload with the detected bank
            await api.uploadStatement(file, detectionResult.bank.id);
            successCount++;
          } else {
            console.log(`❌ Could not detect bank for ${file.name}`);
            detectionFailures++;
            errors.push(`${file.name}: Could not detect bank`);
          }
        } catch (err: any) {
          console.error(`Failed to process ${file.name}:`, err);
          errors.push(`${file.name}: ${err.response?.data?.message || 'Upload failed'}`);
        }
      }

      // Reset upload form
      setFiles([]);
      setDetectedBankName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload transactions
      await loadAllTransactions();

      // Show results
      if (errors.length > 0) {
        setUploadError(
          `Successfully uploaded ${successCount} file(s). ${detectionFailures > 0 ? `${detectionFailures} file(s) failed bank detection. ` : ''}Failed: ${errors.join(', ')}`
        );
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Auto-categorize handler
  const handleAutoCategorize = async () => {
    setAutoCategorizing(true);
    setAutoCategorizeComplete(false);
    try {
      await api.autoCategorizeTransactions();
      await loadAllTransactions();

      // Show "Done!" and keep it (don't revert)
      setAutoCategorizeComplete(true);
      // Note: We no longer use setTimeout to revert the state
      // It will stay "Done" until new transactions are uploaded
    } catch (err: any) {
      console.error('Failed to auto-categorize:', err);
      alert(err.response?.data?.message || 'Failed to auto-categorize transactions');
    } finally {
      setAutoCategorizing(false);
    }
  };

  // Category change handler
  const handleCategoryChange = async (transactionId: string, categoryId: string) => {
    try {
      await api.updateTransactionCategory(transactionId, categoryId);

      // Update local state
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId
            ? {
                ...tx,
                categoryId: categoryId || null,
                category: categoryId ? categories.find((c) => c.id === categoryId) : undefined,
                isCategorized: !!categoryId,
              }
            : tx
        )
      );
    } catch (err: any) {
      console.error('Failed to update category:', err);
      alert('Failed to update category');
    }
  };

  // Delete transaction handler
  const handleDeleteTransaction = async (transactionId: string) => {
    const confirmed = confirm(t('transactions.deleteConfirm'));

    if (!confirmed) return;

    try {
      await api.deleteTransaction(transactionId);

      // Remove from local state
      setTransactions((prev) => prev.filter((tx) => tx.id !== transactionId));
    } catch (err: any) {
      console.error('Failed to delete transaction:', err);
      alert(err.response?.data?.message || 'Failed to delete transaction');
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    setIsAuthenticated(false);
    // Reload to reset state
    window.location.reload();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const categorizedCount = transactions.filter(tx => tx.isCategorized).length;
  const uncategorizedCount = transactions.filter(tx => !tx.isCategorized).length;

  // Toggle month expansion
  const toggleMonth = (monthKey: string) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthKey)) {
        newSet.delete(monthKey);
      } else {
        newSet.add(monthKey);
      }
      return newSet;
    });
  };

  // Group transactions by month
  const transactionsByMonth = useMemo(() => {
    const grouped = new Map<string, Transaction[]>();

    transactions.forEach(tx => {
      const date = new Date(tx.transactionDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!grouped.has(monthKey)) {
        grouped.set(monthKey, []);
      }
      grouped.get(monthKey)!.push(tx);
    });

    // Convert to array and sort by month (most recent first)
    return Array.from(grouped.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([monthKey, txs]) => {
        const date = new Date(monthKey + '-01');
        const monthLabel = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long'
        });

        // Calculate month stats
        const expenses = txs.filter(t => Number(t.amount) < 0)
          .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
        const income = txs.filter(t => Number(t.amount) >= 0)
          .reduce((sum, t) => sum + Number(t.amount), 0);

        // Calculate categorization percentage
        const categorizedCount = txs.filter(t => t.isCategorized).length;
        const categorizedPercentage = txs.length > 0 ? (categorizedCount / txs.length) * 100 : 0;

        return {
          monthKey,
          monthLabel,
          transactions: txs,
          count: txs.length,
          expenses,
          income,
          net: income - expenses,
          categorizedCount,
          categorizedPercentage
        };
      });
  }, [transactions]);

  // Auto-expand only the most recent month on initial load or when new transactions are uploaded
  useEffect(() => {
    if (transactionsByMonth.length > 0) {
      const currentCount = transactions.length;
      const prevCount = prevTransactionCountRef.current;

      // Only auto-expand if:
      // 1. Initial load (prevCount is 0)
      // 2. New transactions were added (count increased)
      if (prevCount === 0 || currentCount > prevCount) {
        const mostRecentMonth = transactionsByMonth[0].monthKey;
        setExpandedMonths(new Set([mostRecentMonth]));

        // Reset auto-categorize complete state when new transactions are uploaded
        if (prevCount > 0 && currentCount > prevCount) {
          setAutoCategorizeComplete(false);
        }
      }

      // Update the ref for next comparison
      prevTransactionCountRef.current = currentCount;
    }
  }, [transactionsByMonth, transactions.length]);

  // Calculate summary stats
  const totalExpenses = transactions
    .filter(tx => Number(tx.amount) < 0)
    .reduce((sum, tx) => sum + Math.abs(Number(tx.amount)), 0);

  const totalIncome = transactions
    .filter(tx => Number(tx.amount) >= 0)
    .reduce((sum, tx) => sum + Number(tx.amount), 0);

  const netBalance = totalIncome - totalExpenses;

  // Extract available months from transactions for filter
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    transactions.forEach(tx => {
      const date = new Date(tx.transactionDate);
      months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
    });
    return ['all-time', ...Array.from(months).sort().reverse()];
  }, [transactions]);

  // Compute chart data
  const expenseData = useMemo(() => {
    // Filter transactions by selected period
    const filteredTransactions = selectedPeriod === 'all-time'
      ? transactions
      : transactions.filter(tx => {
          const date = new Date(tx.transactionDate);
          const txMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          return txMonth === selectedPeriod;
        });

    const categoryTotals = new Map<string, { name: string; value: number; icon: string; color: string }>();

    filteredTransactions
      .filter(tx => tx.isCategorized && Number(tx.amount) < 0)
      .forEach(tx => {
        const category = categories.find(c => c.id === tx.categoryId);
        if (category) {
          const existing = categoryTotals.get(category.id);
          const amount = Math.abs(Number(tx.amount));
          if (existing) {
            existing.value += amount;
          } else {
            categoryTotals.set(category.id, {
              name: `${category.icon || ''} ${category.name}`,
              value: amount,
              icon: category.icon || '',
              color: category.color || '#3B82F6',
            });
          }
        }
      });

    const result = Array.from(categoryTotals.values()).sort((a, b) => b.value - a.value);
    console.log('Expense Data:', result);
    return result;
  }, [transactions, categories, selectedPeriod]);

  const incomeData = useMemo(() => {
    // Filter transactions by selected period
    const filteredTransactions = selectedPeriod === 'all-time'
      ? transactions
      : transactions.filter(tx => {
          const date = new Date(tx.transactionDate);
          const txMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          return txMonth === selectedPeriod;
        });

    const categoryTotals = new Map<string, { name: string; value: number; icon: string; color: string }>();

    filteredTransactions
      .filter(tx => tx.isCategorized && Number(tx.amount) >= 0)
      .forEach(tx => {
        const category = categories.find(c => c.id === tx.categoryId);
        if (category) {
          const existing = categoryTotals.get(category.id);
          const amount = Number(tx.amount);
          if (existing) {
            existing.value += amount;
          } else {
            categoryTotals.set(category.id, {
              name: `${category.icon || ''} ${category.name}`,
              value: amount,
              icon: category.icon || '',
              color: category.color || '#10B981',
            });
          }
        }
      });

    const result = Array.from(categoryTotals.values()).sort((a, b) => b.value - a.value);
    console.log('Income Data:', result);
    return result;
  }, [transactions, categories, selectedPeriod]);

  // Compute trends data (monthly aggregation)
  const trendsData = useMemo(() => {
    const monthlyData = new Map<string, { month: string, expenses: number, income: number }>();

    transactions.forEach(tx => {
      const date = new Date(tx.transactionDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      const existing = monthlyData.get(monthKey) || { month: monthKey, expenses: 0, income: 0 };

      const amount = Math.abs(Number(tx.amount));
      if (Number(tx.amount) < 0) {
        existing.expenses += amount;
      } else {
        existing.income += amount;
      }

      monthlyData.set(monthKey, existing);
    });

    // Convert to array and sort by month
    const result = Array.from(monthlyData.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(item => ({
        ...item,
        monthLabel: new Date(item.month + '-01').toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        })
      }));

    console.log('Trends Data:', result);
    return result;
  }, [transactions]);

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0" style={{
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}></div>

      {/* Accent Lines */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent"></div>
      <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-blue-500 to-transparent"></div>

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header */}
      <div className="relative z-10 border-b-4 border-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4">
            <div>
              <div className="flex items-baseline space-x-2 sm:space-x-4 mb-2">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none">
                  DIRHA
                </h1>
                <div className="text-xs sm:text-sm font-mono text-gray-400 mb-2">v2.0</div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="h-2 w-2 sm:h-3 sm:w-3 bg-emerald-500 animate-pulse"></div>
                <p className="text-xs sm:text-sm font-mono text-gray-400 uppercase tracking-wider sm:tracking-widest">
                  {t('header.tagline')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {isAuthenticated ? (
                /* Logout Button */
                <button
                  onClick={handleLogout}
                  className="relative px-4 sm:px-6 py-3 bg-red-500 text-white font-black text-sm sm:text-base border-4 border-red-500 transition-transform hover:translate-x-1 hover:-translate-y-1 min-h-[44px] flex-1 sm:flex-none"
                >
                  <div className="absolute -top-1 -right-1 w-full h-full bg-red-700 -z-10"></div>
                  {t('header.logout')}
                </button>
              ) : (
                <>
                  {/* Sign In Button */}
                  <a
                    href="/login?mode=signin"
                    className="relative px-4 sm:px-6 py-3 bg-white text-black font-black text-sm sm:text-base border-4 border-white transition-transform hover:translate-x-1 hover:-translate-y-1 min-h-[44px] w-full sm:w-52 text-center"
                  >
                    <div className="absolute -top-1 -right-1 w-full h-full bg-gray-400 -z-10"></div>
                    {t('header.signIn')}
                  </a>
                  {/* Sign Up Button */}
                  <a
                    href="/login?mode=signup"
                    className="relative px-4 sm:px-6 py-3 bg-emerald-500 text-white font-black text-sm sm:text-base border-4 border-emerald-500 transition-transform hover:translate-x-1 hover:-translate-y-1 min-h-[44px] w-full sm:w-52 text-center"
                  >
                    <div className="absolute -top-1 -right-1 w-full h-full bg-emerald-700 -z-10"></div>
                    {t('header.signUp')}
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
      {/* Upload Section */}
      <div className="relative bg-white border-4 border-black p-4 sm:p-6 lg:p-8">
        <div className="absolute -top-1 -left-1 w-full h-full bg-cyan-400 -z-10"></div>

        <div className="border-b-4 border-black pb-4 sm:pb-6 mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-black mb-2 tracking-tight">{t('upload.title')}</h2>
          <p className="text-xs sm:text-sm font-mono text-gray-600 uppercase tracking-wider">
            {t('upload.subtitle')}
          </p>
        </div>

        {/* Detected Bank */}
        {detectedBankName && (
          <div className="bg-emerald-500 border-4 border-black p-4 mb-4 flex items-center space-x-3">
            <div className="text-3xl">✓</div>
            <div>
              <p className="text-xs font-mono text-white uppercase tracking-widest">{t('upload.bankDetected')}</p>
              <p className="text-white font-black text-xl">{detectedBankName}</p>
            </div>
          </div>
        )}

        {/* Detecting */}
        {detecting && (
          <div className="bg-blue-500 border-4 border-black p-4 mb-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-4 border-white"></div>
            <p className="text-white font-black uppercase tracking-wider">{t('upload.detecting')}</p>
          </div>
        )}

        {/* Drop Zone */}
        <div
          className={`border-4 border-dashed p-12 text-center transition-all ${
            dragActive
              ? 'border-black bg-blue-100'
              : 'border-black/30 hover:border-black/50 bg-gray-50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="space-y-6">
            <div className="text-7xl">📄</div>

            {files.length === 0 ? (
              <>
                <div>
                  <p className="text-black font-black text-xl mb-2 uppercase tracking-tight">
                    {t('upload.dragHere')}
                  </p>
                  <p className="text-sm font-mono text-gray-500 uppercase tracking-wider">{t('upload.multipleSupported')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="relative px-6 sm:px-8 py-3 sm:py-4 bg-black text-white font-black text-sm sm:text-base border-4 border-black transition-transform hover:translate-x-1 hover:-translate-y-1 min-h-[44px]"
                  disabled={uploading}
                >
                  <div className="absolute -top-1 -right-1 w-full h-full bg-gray-700 -z-10"></div>
                  {t('upload.chooseFiles')}
                </button>
              </>
            ) : (
              <div className="space-y-4 w-full">
                <div className="text-left">
                  <p className="text-sm font-mono text-gray-500 uppercase tracking-wider mb-3">
                    {files.length} {t('upload.filesSelected', { count: files.length })} {t('upload.selected')}
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="relative flex items-center justify-between bg-white border-2 border-black px-4 py-3"
                      >
                        <div className="absolute -bottom-0.5 -right-0.5 w-full h-full bg-gray-200 -z-10"></div>
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="text-2xl">✓</div>
                          <div className="text-left flex-1 min-w-0">
                            <p className="font-black text-black truncate">{file.name}</p>
                            <p className="text-xs font-mono text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            console.log('🗑️ Removing file:', file.name);
                            const newFiles = files.filter((_, i) => i !== index);
                            setFiles(newFiles);

                            // Clear all state when removing a file
                            setUploadError(null);
                            setFailedStatementFile(null);
                            setDetectedBankName('');

                            if (newFiles.length === 0) {
                              // Reset file input when no files left
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }
                          }}
                          className="px-3 py-1 bg-red-500 text-white text-xs font-black border-2 border-black hover:bg-red-600 ml-3"
                          disabled={uploading}
                        >
                          {t('upload.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    console.log('🗑️ Clearing all files');
                    setFiles([]);
                    setUploadError(null);
                    setFailedStatementFile(null);
                    setDetectedBankName('');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-black border-2 border-black hover:bg-red-600"
                  disabled={uploading}
                >
                  {t('upload.clearAll')}
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={() => {
            console.log('🚀 Upload clicked. State:', { filesLength: files.length, uploading });
            handleUpload();
          }}
          disabled={files.length === 0 || uploading}
          className="relative w-full mt-4 sm:mt-6 px-4 sm:px-8 py-4 bg-black text-white font-black text-base sm:text-lg border-4 border-black transition-transform hover:translate-x-1 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 flex items-center justify-center space-x-2 sm:space-x-3 min-h-[56px]"
        >
          <div className="absolute -top-1 -right-1 w-full h-full bg-gray-700 -z-10"></div>
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-b-4 border-white"></div>
              <span className="hidden sm:inline">{t('upload.processing')} {files.length} {t('upload.filesSelected', { count: files.length })}...</span>
              <span className="inline sm:hidden">{t('upload.processing')}...</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">{t('upload.uploadButton')}</span>
              <span className="inline sm:hidden">{t('upload.uploadButtonMobile')}</span>
              <span className="text-xl sm:text-2xl">🚀</span>
            </>
          )}
        </button>

        {/* Upload Error */}
        {uploadError && (
          <div className="mt-4 p-4 bg-red-500 border-4 border-black">
            <div className="flex items-start space-x-3 mb-4">
              <div className="text-3xl">❌</div>
              <div className="flex-1">
                <h3 className="font-black text-white uppercase tracking-wider">{t('upload.error')}</h3>
                <p className="text-white text-sm mt-1 font-mono">{uploadError}</p>
              </div>
            </div>

            {/* Show "Request New Bank" button only when detection fails */}
            {uploadError.includes('detect') && (
              <div className="mt-4 pt-4 border-t-2 border-white/30">
                <p className="text-xs font-mono text-white/80 uppercase tracking-wider mb-3">
                  {t('upload.notSupported')}
                </p>
                <button
                  onClick={() => setBankRequestModalOpen(true)}
                  className="relative px-6 py-3 bg-white text-black font-black border-4 border-white transition-transform hover:translate-x-1 hover:-translate-y-1"
                >
                  <div className="absolute -top-1 -right-1 w-full h-full bg-gray-300 -z-10"></div>
                  🏦 {t('upload.requestNewBank')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bank Request Modal */}
      <BankRequestModal
        isOpen={bankRequestModalOpen}
        onClose={() => {
          setBankRequestModalOpen(false);
          setFailedStatementFile(null);
        }}
        preloadedFile={failedStatementFile}
      />

      {/* Stats Overview - Brutalist Data Cards */}
      {transactions.length > 0 && (
        <div className="space-y-4">
          {/* Primary Metrics Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Transactions */}
            <div className="relative bg-white border-4 border-black p-6 group hover:translate-x-1 hover:-translate-y-1 hover:bg-blue-500 transition-all">
              <div className="absolute -top-1 -right-1 w-full h-full bg-blue-500 -z-10"></div>
              <div className="text-xs font-mono text-gray-500 group-hover:text-white uppercase tracking-widest mb-2 transition-colors">{t('stats.totalTransactions')}</div>
              <div className="text-6xl font-black text-black group-hover:text-white mb-2 leading-none transition-colors">{transactions.length}</div>
              <div className="h-1 bg-black group-hover:bg-white mb-2 transition-colors"></div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-gray-600 group-hover:text-white transition-colors">{t('stats.categorized')}</span>
                <span className="text-sm font-black group-hover:text-white transition-colors">{categorizedCount}/{transactions.length}</span>
              </div>
              <div className="mt-2 bg-black group-hover:bg-white h-2 w-full transition-colors">
                <div
                  className="bg-blue-500 group-hover:bg-blue-700 h-full transition-all duration-500"
                  style={{ width: `${(categorizedCount/transactions.length)*100}%` }}
                ></div>
              </div>
            </div>

            {/* Income Card */}
            <div className="relative bg-white border-4 border-black p-6 group hover:translate-x-1 hover:-translate-y-1 hover:bg-emerald-500 transition-all overflow-hidden">
              <div className="absolute -top-1 -right-1 w-full h-full bg-emerald-500 -z-10"></div>
              <div className="text-xs font-mono text-gray-500 group-hover:text-white uppercase tracking-widest mb-2 transition-colors">{t('stats.totalIncome')}</div>
              <div className="text-4xl sm:text-5xl md:text-6xl font-black text-emerald-600 group-hover:text-white mb-2 leading-none transition-colors break-all">
                +{(totalIncome/1000).toFixed(1)}k
              </div>
              <div className="h-1 bg-black group-hover:bg-white mb-2 transition-colors"></div>
              <div className="text-xs font-mono text-gray-600 group-hover:text-white transition-colors truncate">
                {totalIncome.toFixed(2)} MAD
              </div>
            </div>

            {/* Expenses Card */}
            <div className="relative bg-white border-4 border-black p-6 group hover:translate-x-1 hover:-translate-y-1 hover:bg-red-500 transition-all overflow-hidden">
              <div className="absolute -top-1 -right-1 w-full h-full bg-red-500 -z-10"></div>
              <div className="text-xs font-mono text-gray-500 group-hover:text-white uppercase tracking-widest mb-2 transition-colors">{t('stats.totalExpenses')}</div>
              <div className="text-4xl sm:text-5xl md:text-6xl font-black text-red-600 group-hover:text-white mb-2 leading-none transition-colors break-all">
                -{(totalExpenses/1000).toFixed(1)}k
              </div>
              <div className="h-1 bg-black group-hover:bg-white mb-2 transition-colors"></div>
              <div className="text-xs font-mono text-gray-600 group-hover:text-white transition-colors truncate">
                {totalExpenses.toFixed(2)} MAD
              </div>
            </div>
          </div>

          {/* Big Number - Net Balance */}
          <div className={`relative border-8 p-6 sm:p-8 md:p-12 group hover:translate-x-2 hover:-translate-y-2 transition-transform overflow-hidden ${
            netBalance >= 0
              ? 'bg-emerald-500 border-black'
              : 'bg-red-500 border-black'
          }`}>
            <div className={`absolute -top-2 -right-2 w-full h-full -z-10 ${
              netBalance >= 0 ? 'bg-emerald-700' : 'bg-red-700'
            }`}></div>
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm font-mono text-white/80 uppercase tracking-wider sm:tracking-[0.3em] mb-3 truncate">
                  {t('stats.netPosition')}
                </div>
                <div className="text-5xl sm:text-7xl md:text-9xl font-black text-white tracking-tighter leading-none break-all">
                  {netBalance >= 0 ? '+' : ''}{(netBalance/1000).toFixed(1)}K
                </div>
                <div className="text-lg sm:text-xl md:text-2xl font-mono text-white/90 mt-2 truncate">
                  {netBalance.toFixed(2)} MAD
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-4xl sm:text-5xl md:text-6xl font-black px-4 sm:px-6 py-2 sm:py-3 border-4 border-white ${
                  netBalance >= 0 ? 'bg-emerald-600' : 'bg-red-600'
                } text-white`}>
                  {netBalance >= 0 ? '↑' : '↓'}
                </div>
                <div className="text-xs font-mono text-white/80 mt-2 uppercase tracking-widest">
                  {netBalance >= 0 ? t('stats.surplus') : t('stats.deficit')}
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Transactions Section */}
      <div className="relative bg-white border-4 border-black p-4 sm:p-6 lg:p-8">
        <div className="absolute -top-1 -left-1 w-full h-full bg-yellow-400 -z-10"></div>

        <div className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b-4 border-black">
          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4">
            <div className="w-full sm:w-auto">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-black mb-3 sm:mb-4 tracking-tighter">
                {t('transactions.title')}
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="px-3 sm:px-4 py-2 bg-black text-white font-mono text-xs font-bold">
                  {t('transactions.total')}: {transactions.length}
                </div>
                <div className="px-3 sm:px-4 py-2 bg-emerald-500 text-white font-mono text-xs font-bold">
                  {t('transactions.categorized')}: {categorizedCount}
                </div>
                {uncategorizedCount > 0 && (
                  <div className="px-3 sm:px-4 py-2 bg-red-500 text-white font-mono text-xs font-bold animate-pulse">
                    {t('transactions.pending')}: {uncategorizedCount}
                  </div>
                )}
              </div>
            </div>

            {uncategorizedCount > 0 && (
              <button
                onClick={handleAutoCategorize}
                disabled={autoCategorizing || autoCategorizeComplete}
                className={`relative w-full sm:w-auto px-6 sm:px-8 py-4 font-black text-base sm:text-lg border-4 border-black transition-transform hover:translate-x-1 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 min-h-[56px] ${
                  autoCategorizeComplete
                    ? 'bg-emerald-500 text-white'
                    : 'bg-blue-500 text-white'
                }`}
              >
                <div className={`absolute -top-1 -right-1 w-full h-full -z-10 ${
                  autoCategorizeComplete ? 'bg-emerald-700' : 'bg-blue-700'
                }`}></div>
                {autoCategorizing ? (
                  <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-4 border-white"></div>
                    <span className="hidden sm:inline">{t('transactions.analyzing')}</span>
                    <span className="inline sm:hidden">{t('transactions.analyzingMobile')}</span>
                  </div>
                ) : autoCategorizeComplete ? (
                  <span>✓ {t('transactions.complete')}</span>
                ) : (
                  <>
                    <span className="hidden sm:inline">⚡ {t('transactions.smartCategorize')}</span>
                    <span className="inline sm:hidden">⚡ {t('transactions.smartCategorizeMobile')}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-black"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-16 border-4 border-dashed border-black/20">
            <div className="text-7xl mb-4">📊</div>
            <h3 className="text-2xl font-black text-black mb-2">{t('transactions.noData')}</h3>
            <p className="text-gray-600 font-mono text-sm">{t('transactions.noDataSubtitle')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactionsByMonth.map((monthGroup) => (
              <div key={monthGroup.monthKey} className="border-4 border-black bg-white">
                {/* Month Header - Clickable */}
                <button
                  onClick={() => toggleMonth(monthGroup.monthKey)}
                  className="w-full p-4 sm:p-6 bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      {/* Expand/Collapse Icon */}
                      <div className="text-xl sm:text-2xl font-black">
                        {expandedMonths.has(monthGroup.monthKey) ? '▼' : '▶'}
                      </div>

                      {/* Month Label */}
                      <div className="text-left">
                        <h3 className="text-lg sm:text-2xl font-black uppercase tracking-tight">
                          {monthGroup.monthLabel}
                        </h3>
                        <p className="text-xs font-mono text-gray-400 uppercase tracking-wider">
                          {monthGroup.count} {t('transactions.transaction', { count: monthGroup.count })} (
                          <span className={
                            monthGroup.categorizedPercentage === 100
                              ? 'text-emerald-400'
                              : monthGroup.categorizedPercentage >= 50
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }>
                            {monthGroup.categorizedPercentage.toFixed(0)}% {t('transactions.categorizedPercentage')}
                          </span>
                          )
                        </p>
                      </div>
                    </div>

                    {/* Month Summary Stats */}
                    <div className="flex items-center gap-2 sm:gap-4 ml-8 sm:ml-0">
                      {/* Income */}
                      {monthGroup.income > 0 && (
                        <div className="px-2 sm:px-4 py-1 sm:py-2 bg-emerald-500 border-2 border-white">
                          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-wider">{t('transactions.income')}</div>
                          <div className="text-sm sm:text-lg font-black">+{(monthGroup.income/1000).toFixed(1)}k</div>
                        </div>
                      )}

                      {/* Expenses */}
                      {monthGroup.expenses > 0 && (
                        <div className="px-2 sm:px-4 py-1 sm:py-2 bg-red-500 border-2 border-white">
                          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-wider">{t('transactions.expenses')}</div>
                          <div className="text-sm sm:text-lg font-black">-{(monthGroup.expenses/1000).toFixed(1)}k</div>
                        </div>
                      )}
                    </div>
                  </div>
                </button>

                {/* Transactions List - Collapsible */}
                {expandedMonths.has(monthGroup.monthKey) && (
                  <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 bg-gray-50">
                    {monthGroup.transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="relative p-3 sm:p-5 border-2 border-black bg-white hover:translate-x-1 transition-transform group"
                      >
                        <div className="absolute -bottom-0.5 -right-0.5 w-full h-full bg-gray-200 -z-10 group-hover:bg-gray-300"></div>

                        {/* Mobile Layout: Stacked */}
                        <div className="flex flex-col gap-3">
                          {/* Row 1: Date, Amount, Delete */}
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-mono text-gray-500 uppercase tracking-wider">
                              {formatDate(transaction.transactionDate)}
                            </p>
                            <div className="flex items-center gap-2">
                              <div className={`px-3 py-1.5 sm:px-4 sm:py-2 font-black text-base sm:text-xl border-2 border-black ${
                                Number(transaction.amount) < 0
                                  ? 'bg-red-500 text-white'
                                  : 'bg-emerald-500 text-white'
                              }`}>
                                {Number(transaction.amount) < 0 ? '-' : '+'}{Math.abs(Number(transaction.amount)).toFixed(0)}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTransaction(transaction.id);
                                }}
                                className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500 text-white font-black text-lg sm:text-xl border-2 border-black hover:bg-red-600 flex items-center justify-center flex-shrink-0"
                                title="Delete transaction"
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          {/* Row 2: Description */}
                          <p className="font-bold text-black text-sm sm:text-lg leading-tight">
                            {transaction.description}
                          </p>

                          {/* Row 3: Category Dropdown */}
                          <select
                            value={transaction.categoryId || ''}
                            onChange={(e) => handleCategoryChange(transaction.id, e.target.value)}
                            className={`w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-black font-bold text-sm sm:text-base focus:outline-none focus:ring-4 focus:ring-blue-300 ${
                              transaction.isCategorized
                                ? 'bg-white text-black'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <option value="">{t('transactions.uncategorized')}</option>
                            {categories
                              .filter((c) => c.type === (Number(transaction.amount) < 0 ? 'expense' : 'income'))
                              .map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.icon} {category.name.toUpperCase()}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts Section */}
      {categorizedCount > 0 && (
        <div className="relative bg-white border-4 border-black p-4 sm:p-8">
          <div className="absolute -top-1 -right-1 w-full h-full bg-purple-400 -z-10"></div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8 pb-4 border-b-4 border-black">
            <h2 className="text-2xl sm:text-4xl font-black text-black">
              {t('charts.title')}
            </h2>

            {/* Period Selector */}
            <div className="w-full sm:w-auto">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 border-4 border-black font-black text-sm sm:text-base bg-white text-black focus:outline-none focus:ring-4 focus:ring-purple-300 hover:translate-x-0.5 hover:-translate-y-0.5 transition-transform"
              >
                <option value="all-time">📊 {t('charts.periodAllTime')}</option>
                {availableMonths.filter(m => m !== 'all-time').map(month => {
                  const date = new Date(month + '-01');
                  const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                  return (
                    <option key={month} value={month}>
                      📅 {label.toUpperCase()}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Expenses Pie Chart */}
            <div className="border-4 border-black p-6 bg-red-50">
              <h3 className="text-2xl font-black text-black mb-6 uppercase tracking-tight">
                {t('charts.expensesByCategory')}
              </h3>
              {expenseData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={expenseData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        stroke="#000"
                        strokeWidth={3}
                      >
                        {expenseData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => `${value.toFixed(2)} MAD`}
                        contentStyle={{ border: '3px solid black', borderRadius: 0, fontWeight: 'bold' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {expenseData.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border-2 border-black bg-white">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: entry.color }}></div>
                          <span className="font-bold text-sm">{entry.name}</span>
                        </div>
                        <span className="font-black text-sm">{entry.value.toFixed(0)} MAD</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-black/20">
                  <p className="font-mono text-sm text-gray-500">{t('transactions.noData')}</p>
                </div>
              )}
            </div>

            {/* Income Pie Chart */}
            <div className="border-4 border-black p-6 bg-emerald-50">
              <h3 className="text-2xl font-black text-black mb-6 uppercase tracking-tight">
                {t('charts.incomeByCategory')}
              </h3>
              {incomeData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={incomeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        stroke="#000"
                        strokeWidth={3}
                      >
                        {incomeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => `${value.toFixed(2)} MAD`}
                        contentStyle={{ border: '3px solid black', borderRadius: 0, fontWeight: 'bold' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {incomeData.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border-2 border-black bg-white">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: entry.color }}></div>
                          <span className="font-bold text-sm">{entry.name}</span>
                        </div>
                        <span className="font-black text-sm">{entry.value.toFixed(0)} MAD</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-black/20">
                  <p className="font-mono text-sm text-gray-500">{t('transactions.noData')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Trends Chart */}
          {trendsData.length > 1 && (
            <div className="mt-8 border-4 border-black p-6 bg-gray-50">
              <h3 className="text-2xl font-black text-black mb-6 uppercase tracking-tight">
                {t('charts.trendsOverTime')}
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={trendsData}>
                  <defs>
                    <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#000" strokeWidth={2} />
                  <XAxis
                    dataKey="monthLabel"
                    stroke="#000"
                    strokeWidth={2}
                    style={{ fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <YAxis
                    stroke="#000"
                    strokeWidth={2}
                    style={{ fontSize: '12px', fontWeight: 'bold' }}
                    tickFormatter={(value) => `${(value/1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value: number) => `${value.toFixed(0)} MAD`}
                    contentStyle={{
                      border: '3px solid black',
                      borderRadius: 0,
                      fontWeight: 'bold',
                      backgroundColor: 'white'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stroke="#EF4444"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorExpense)"
                    name={t('transactions.expenses')}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#10B981"
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#colorIncome)"
                    name={t('transactions.income')}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="mt-6 flex items-center justify-center space-x-8">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-4 bg-red-500 border-2 border-black"></div>
                  <span className="font-black text-sm uppercase">{t('transactions.expenses')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-4 bg-emerald-500 border-2 border-black"></div>
                  <span className="font-black text-sm uppercase">{t('transactions.income')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Analytics Dashboard Modal */}
      <AnalyticsDashboardModal
        isOpen={analyticsModalOpen}
        onClose={() => setAnalyticsModalOpen(false)}
      />

      {/* Footer */}
      <Footer totalBanks={6} totalTransactions={transactions.length} />
    </div>
  );
}
